/**
 * SIGN IN — the door, beside a quiet view of what is behind it (owner-accepted
 * design, 2026-10-09: "an operating system for professional work", not a
 * reconciliation tool).
 *
 * There is no product tour and no reassurance copy. A person arriving at this
 * screen either has an account or an invitation; the form stays short, and the
 * one line of description is the workspace itself — hidden on narrow screens,
 * where only the door remains. The address
 * they were going to is untouched, so signing in returns them to it rather
 * than to a landing page they did not ask for.
 *
 * WHAT IS NOT DECIDED HERE (R5.1). The rules about an email and a password
 * live in the CONTRACT and are enforced by the server. They are now SHOWN
 * before a person can break one — `PASSWORD_RULES` is read from that same
 * contract, so this is the one copy of the rule rather than a second set to
 * disagree with the first — and every refusal is still the server's own
 * sentence, now shown against the field it is about.
 *
 * The first independent test of this screen typed a short password and was
 * told "The request is not valid", with four fields on screen and no way to
 * tell which one was meant. That is the whole reason this file changed.
 */
import { useState } from "react";
import type { FormEvent } from "react";
import { PASSWORD_MIN_LENGTH, PASSWORD_RULES, SessionResponse, type Principal } from "@leveri/contracts";
import { Button, Notice, TextField, Reading, SERIF_OPSZ } from "../file/index.js";
import { call, failureMessage, fieldFailure } from "../api.js";
import { Wordmark } from "./shell.js";

export interface SignInProps {
  onSignedIn: (principal: Principal) => void;
}

export function SignIn({ onSignedIn }: SignInProps) {
  const [opening, setOpening] = useState(false);
  /** An accounting firm (many clients) or a finance team (its own company) — 0034. */
  const [kind, setKind] = useState<"FIRM" | "FINANCE_TEAM">("FIRM");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  /** The server's sentence per field. Never composed here, only placed. */
  const [fields, setFields] = useState<Record<string, string | null>>({});

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const form = new FormData(event.currentTarget);
    const read = (name: string) => String(form.get(name) ?? "");
    setBusy(true);
    setFailure(null);
    setFields({});
    try {
      const session = opening
        ? await call("POST", "/api/auth/register-firm", SessionResponse, {
            firmName: read("firmName"), displayName: read("displayName"), email: read("email"), password: read("password"), kind,
            ...(read("inviteCode").trim() ? { inviteCode: read("inviteCode") } : {}),
          })
        : await call("POST", "/api/auth/login", SessionResponse, { email: read("email"), password: read("password") });
      // The principal comes back with the session, already parsed by its own
      // contract — so nothing is re-read to confirm who just signed in.
      onSignedIn(session.principal);
    } catch (error) {
      setFailure(failureMessage(error));
      setFields({
        firmName: fieldFailure(error, "firmName"),
        displayName: fieldFailure(error, "displayName"),
        email: fieldFailure(error, "email"),
        password: fieldFailure(error, "password"),
      });
      setBusy(false);
    }
  }

  const switchTo = (next: boolean) => { setOpening(next); setFailure(null); setFields({}); };

  return (
    <div className="flex min-h-screen flex-wrap bg-canvas text-ink">
      <Showcase />

      {/* THE DOOR. Short on purpose: a person arriving here has an account or an
          invitation, and everything else stands between them and the work. */}
      <section className="flex w-full min-w-0 items-center justify-center px-6 py-14 sm:px-14 lg:w-1/2">
        <div className="w-full max-w-[380px]">
          <div className="mb-10 lg:hidden"><Wordmark /></div>
          <h2 className="text-[22px] font-semibold tracking-[-0.01em]">{opening ? "Open your workspace" : "Sign in"}</h2>
          <p className="mt-1.5 text-sm text-stone">{opening ? "Use the invitation the Leveri team sent you." : "Welcome back."}</p>

          <form onSubmit={submit} className="mt-8 flex flex-col gap-5">
            {opening && (
              <>
                {/* PRIVATE BETA (migration 0029). The server decides whether a code is
                    required and whether this one is good; the screen only carries it. */}
                <TextField name="inviteCode" label="Invitation code" placeholder="XXXX-XXXX-XXXX-XXXX" autoComplete="off" className={TALL} />
                {/* WHO IS OPENING IT. A firm works for clients; a finance team works for its own company,
                    which becomes its one client. Two choices, side by side, the chosen one ruled in ink. */}
                <fieldset>
                  <legend className="mb-1.5 text-sm font-medium text-ink-soft">This workspace is for</legend>
                  <div className="grid grid-cols-2 gap-2">
                    {KINDS.map((k) => (
                      <label key={k.value} className={`cursor-pointer rounded-control border px-3 py-2.5 ${kind === k.value ? "border-ink bg-raised" : "border-line-strong hover:bg-band"}`}>
                        <input type="radio" name="kind" value={k.value} checked={kind === k.value} onChange={() => setKind(k.value)} className="sr-only" data-testid={`kind-${k.value}`} />
                        <span className="block text-[15px] text-ink">{k.label}</span>
                        <span className="mt-0.5 block text-[13px] leading-snug text-stone">{k.detail}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <TextField name="firmName" label={kind === "FIRM" ? "Firm name" : "Company name"} required autoComplete="organization" className={TALL} />
                {fields["firmName"] && <FieldNotice>{fields["firmName"]}</FieldNotice>}
                <TextField name="displayName" label="Your name" required autoComplete="name" className={TALL} />
                {fields["displayName"] && <FieldNotice>{fields["displayName"]}</FieldNotice>}
              </>
            )}
            <TextField name="email" type="email" label="Work email" required autoComplete="username" placeholder="name@firm.com" className={TALL} />
            {fields["email"] && <FieldNotice>{fields["email"]}</FieldNotice>}
            <TextField
              name="password"
              type="password"
              label="Password"
              /* THE RULE, BEFORE IT CAN BE BROKEN — read from the contract both
                 sides share; shown only when opening, and said once. */
              hint={opening ? PASSWORD_RULES.join(" ") : undefined}
              required
              minLength={opening ? PASSWORD_MIN_LENGTH : undefined}
              autoComplete={opening ? "new-password" : "current-password"}
              className={TALL}
            />
            {fields["password"] && <FieldNotice>{fields["password"]}</FieldNotice>}

            {failure && !Object.values(fields).some((f) => f === failure) && <Notice>{failure}</Notice>}

            <Button type="submit" disabled={busy} className="mt-1 !h-11 w-full !text-[15px]">
              {opening ? "Open the workspace" : "Sign in"}
            </Button>
            {busy && <Reading>{opening ? "Opening the workspace…" : "Signing in…"}</Reading>}
          </form>

          <div className="mt-8 border-t border-line pt-6 text-sm text-stone">
            {opening ? (
              <>Already have an account?{" "}
                <button type="button" disabled={busy} onClick={() => switchTo(false)} className="font-medium text-ink underline-offset-4 hover:underline">Sign in</button>
              </>
            ) : (
              <>
                <div>Have an invitation?{" "}
                  <button type="button" disabled={busy} onClick={() => switchTo(true)} className="font-medium text-ink underline-offset-4 hover:underline">Open your firm or team</button>
                </div>
                <div className="mt-1.5">Accounts are by invitation for now.</div>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const KINDS = [
  { value: "FIRM", label: "An accounting firm", detail: "Work for many clients." },
  { value: "FINANCE_TEAM", label: "A finance team", detail: "Close your own company's books." },
] as const;

/** The door's controls are taller than the product's dense 34px: a first impression, not a working grid. */
const TALL = "!h-11 !text-[15px]";

/**
 * WHAT LEVERI IS, SHOWN RATHER THAN SAID. Not a reconciliation: the workspace —
 * many kinds of work across clients, what waits on whom, and sign-off. The
 * names are illustrative and say so by being plainly generic.
 */
function Showcase() {
  return (
    <section className="hidden w-1/2 min-w-0 flex-col justify-between gap-12 border-r border-line px-[72px] py-14 lg:flex">
      <div><Wordmark /></div>

      <div className="flex max-w-[620px] flex-col gap-9">
        <div>
          <h1 className="font-serif text-[46px] font-normal leading-[1.12] tracking-[-0.02em]" style={SERIF_OPSZ}>
            The operating system for professional accounting work.
          </h1>
          <p className="mt-5 max-w-[540px] text-[17px] leading-relaxed text-ink-soft">
            Work, evidence, client requests and sign-off in one place. Every figure traced to its source; nothing final until an accountant signs it.
          </p>
        </div>

        <div className="border border-line bg-raised px-6 pb-2 pt-1">
          <div className="flex items-baseline justify-between py-3">
            <span className="text-xs uppercase tracking-[0.04em] text-stone">This week</span>
            <span className="text-xs text-stone">4 clients · 2 waiting on you</span>
          </div>
          {SHOWCASE.map((row) => (
            <div key={row.client} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)_9.5rem] items-center gap-4 border-t border-line py-3.5 text-sm">
              <span className="font-medium">{row.client}</span>
              <span className="text-ink-soft">{row.work}</span>
              <span className={`justify-self-end whitespace-nowrap rounded-tag px-2 py-1 text-xs ${row.tone}`}>{row.state}</span>
            </div>
          ))}
        </div>
      </div>

      <p className="text-[13px] text-stone">Built by a team from finance and technology.</p>
    </section>
  );
}

const SHOWCASE = [
  { client: "Northwind Foods", work: "Accounts payable · August", state: "Needs your review", tone: "bg-forest-tint text-forest" },
  { client: "Acme Trading", work: "Bank · September", state: "Waiting on client", tone: "bg-att-tint text-att-text" },
  { client: "Global Supply Co", work: "Supplier statement requested", state: "Sent 2 Sep", tone: "bg-prog-tint text-prog-text" },
  { client: "Harbour & Co", work: "Workpaper · Q3 payables", state: "Signed", tone: "bg-est-tint text-est-text" },
] as const;

/** One field's refusal, in the server's own words, under the field it is about. */
function FieldNotice({ children }: { children: string }) {
  return <p className="-mt-2 text-[13px] text-con-text">{children}</p>;
}
